import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck } from 'lucide-react';
import { soundManager } from '../lib/sound';
import { updateConsent } from '../lib/analytics';
import { useTranslation } from '../lib/translations';

interface CookieBannerProps {
  onOpenPolicy: () => void;
  isPolicyOpen?: boolean;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({ onOpenPolicy, isPolicyOpen = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { language } = useTranslation();

  useEffect(() => {
    const checkConsent = () => {
      try {
        const hasConsented = localStorage.getItem('cookie_consent');
        if (!hasConsented) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } catch {
        // In restricted environments where localStorage is blocked, do nothing 
      }
    };

    const timer = setTimeout(() => {
      checkConsent();
    }, 800);

    const handleConsentEvent = (e: CustomEvent) => {
      if (e.detail?.active === false) {
        setIsVisible(false);
        setIsDetailsOpen(false);
      }
    };

    window.addEventListener('cookie-banner-change' as any, handleConsentEvent);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('cookie-banner-change' as any, handleConsentEvent);
    };
  }, []);

  const handleAccept = () => {
    soundManager.play('click');
    try {
      localStorage.setItem('cookie_consent', 'true');
    } catch {
      // Ignore if blocked
    }
    try {
      updateConsent(true);
    } catch {
      // Ignore
    }
    setIsVisible(false);
    setIsDetailsOpen(false);
    window.dispatchEvent(new CustomEvent('cookie-banner-change', { detail: { active: false } }));
  };

  const handleDecline = () => {
    soundManager.play('click');
    try {
      localStorage.setItem('cookie_consent', 'false');
    } catch {
      // Ignore if blocked
    }
    try {
      updateConsent(false);
    } catch {
      // Ignore
    }
    setIsVisible(false);
    setIsDetailsOpen(false);
    window.dispatchEvent(new CustomEvent('cookie-banner-change', { detail: { active: false } }));
  };

  const handleToggleDetails = () => {
    soundManager.play('click');
    setIsDetailsOpen((prev) => !prev);
  };

  const handleOpenFullPolicy = (e: React.MouseEvent) => {
    e.stopPropagation();
    soundManager.play('click');
    onOpenPolicy();
  };

  return (
    <AnimatePresence>
      {isVisible && !isPolicyOpen && (
        <motion.div
          layout
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed bottom-2 md:bottom-4 inset-x-2 md:inset-x-auto md:right-4 md:w-[420px] max-w-full z-[9999] pointer-events-none"
        >
          <div className="bg-slate-900/95 backdrop-blur-md border border-white/10 rounded-2xl p-3.5 md:p-4 shadow-[0_12px_40px_rgba(0,0,0,0.65)] pointer-events-auto flex flex-col gap-3 transition-all">
            {/* Header row */}
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0 border border-cyan-500/20 mt-0.5">
                <ShieldCheck className="w-4 h-4 text-cyan-400" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-black text-white uppercase tracking-wider">
                    {language === 'tr' ? 'Gizlilik ve Çerezler' : 'Privacy & Cookies'}
                  </p>
                  <button
                    type="button"
                    onClick={handleToggleDetails}
                    className="text-[11px] text-cyan-300 hover:text-cyan-200 font-bold hover:underline cursor-pointer flex items-center gap-0.5 shrink-0"
                  >
                    {isDetailsOpen
                      ? (language === 'tr' ? 'Daha Az ▴' : 'Show Less ▴')
                      : (language === 'tr' ? 'Detaylar ▾' : 'Details ▾')
                    }
                  </button>
                </div>

                <p className="text-[11px] sm:text-xs text-slate-200 leading-normal mt-1">
                  {language === 'tr' ? (
                    <>
                      Deneyiminizi geliştirmek ve istatistikleri ölçmek için çerezler kullanıyoruz.{' '}
                      {!isDetailsOpen && (
                        <span
                          onClick={handleToggleDetails}
                          className="text-slate-300 hover:text-white cursor-pointer text-[10px] underline ml-1"
                        >
                          (Devamını oku)
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      We use cookies to enhance your experience and measure visitor statistics.{' '}
                      {!isDetailsOpen && (
                        <span
                          onClick={handleToggleDetails}
                          className="text-slate-300 hover:text-white cursor-pointer text-[10px] underline ml-1"
                        >
                          (Read more)
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Expandable detailed paragraph (Shown inline without modal) */}
            <AnimatePresence>
              {isDetailsOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-2.5 rounded-xl bg-slate-950/60 border border-white/5 text-[11px] text-slate-300 leading-relaxed">
                    {language === 'tr' ? (
                      <>
                        6698 sayılı KVKK kapsamında; zorunlu çerezler dışındaki Google Analytics analitik çerezleri yalnızca açık rızanızla (Kabul Et) etkinleştirilir. Tercihinizi dilediğiniz zaman değiştirebilir ve detaylar için{' '}
                        <button
                          type="button"
                          onClick={handleOpenFullPolicy}
                          className="text-cyan-300 hover:underline font-semibold cursor-pointer inline"
                        >
                          Çerez Politikamızı
                        </button>{' '}
                        inceleyebilirsiniz.
                      </>
                    ) : (
                      <>
                        In compliance with KVKK and GDPR, non-essential Google Analytics cookies are only activated with your explicit consent (Accept). You can change your preference anytime and review our{' '}
                        <button
                          type="button"
                          onClick={handleOpenFullPolicy}
                          className="text-cyan-300 hover:underline font-semibold cursor-pointer inline"
                        >
                          Cookie Policy
                        </button>{' '}
                        for full details.
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-1 border-t border-white/5 justify-end">
              <button
                type="button"
                onClick={handleDecline}
                className="flex-1 sm:flex-none px-4 py-1.5 border border-white/20 hover:bg-white/5 text-slate-200 hover:text-white text-[11px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
              >
                {language === 'tr' ? 'Reddet' : 'Decline'}
              </button>
              <button
                type="button"
                onClick={handleAccept}
                className="flex-1 sm:flex-none px-5 py-1.5 bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-[11px] font-black uppercase tracking-wider rounded-lg transition-all shadow-md shadow-cyan-500/20 active:scale-95 cursor-pointer text-center"
              >
                {language === 'tr' ? 'Kabul Et' : 'Accept'}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
